import React, { useState, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { HiOutlineSearch, HiOutlineBell, HiOutlineMenu, HiX, HiChevronDown } from "react-icons/hi";
import { logo } from "../../constants/icons";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import MegaMenuMobile from "./MegaMenuMobile";
import ProfileSidebar from "./ProfileSidebar";
import NotificationSidebar from "./NotificationSidebar";

const MobileHeader = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isMobileProfileOpen,
  setIsMobileProfileOpen,
  isMobileSearchOpen,
  setIsMobileSearchOpen,
  isNotificationOpen,
  setIsNotificationOpen,
  unreadCount,
  profileData,
  getProfileDisplay,
  getProfilePicAccordingToRole,
  getInitialsForRole,
  getNameByRole,
  handleLogout,
  menuItems,
  navigationItems,
  megaCategories,
  quickAccessPills,
  isActive,
  handleRoleSwitch,
  canSwitchRoles,
  currentRole,
  isAgencyOnUI,
  isRoleSwitching,
  searchQuery,
  setSearchQuery,
  handleSearch,
  searchType,
  setSearchType,
  searchTypes,
  notifications,
  recentNotifications,
  handleNotificationClick,
  handleMarkAllAsRead,
  formatNotificationTime,
}) => {
  const [isMobileSearchTypeOpen, setIsMobileSearchTypeOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const mobileProfileImage = getProfilePicAccordingToRole
    ? getProfilePicAccordingToRole(currentRole, profileData)
    : null;
  const mobileProfileInitials = getInitialsForRole
    ? getInitialsForRole(currentRole, profileData)
    : "";
  const mobileProfileName = getNameByRole
    ? getNameByRole(currentRole, profileData)
    : "";

  // Anchor the mobile mega-menu to the live bottom edge of the mobile header
  // so it opens flush (no gap/overlap) regardless of the header's height.
  const mobileHeaderRef = useRef(null);
  const [menuAnchorTop, setMenuAnchorTop] = useState(0);
  useLayoutEffect(() => {
    const el = mobileHeaderRef.current;
    if (!el) return undefined;
    const measure = () => setMenuAnchorTop(el.getBoundingClientRect().bottom);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, []);

  return (
    <>
      <div ref={mobileHeaderRef} className="md:hidden flex items-center justify-between w-full h-16 px-4">
        {!isMobileSearchOpen ? (
          <>
            <Link to="/" className="flex-shrink-0">
              <img src={logo} alt="TransferPitch" className="h-8 w-auto" />
            </Link>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMobileSearchOpen(true)}
                className="p-2"
                aria-label="Open search"
              >
                <HiOutlineSearch className="w-6 h-6" />
              </button>
              <button
                onClick={() => {
                  const next = !isNotificationOpen;
                  setIsNotificationOpen(next);
                  if (next) {
                    window.dispatchEvent(new CustomEvent("notifications:connect"));
                  } else {
                    window.dispatchEvent(new CustomEvent("notifications:disconnect"));
                  }
                }}
                className="p-2 relative"
                aria-label="Toggle notifications"
              >
                <HiOutlineBell className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2"
                aria-label="Open menu"
              >
                <HiOutlineMenu className="w-6 h-6" />
              </button>
              <button
                onClick={() => setIsMobileProfileOpen(true)}
                className="p-1"
                aria-label="Open profile menu"
              >
                {mobileProfileImage ? (
                  <img
                    src={mobileProfileImage}
                    alt={mobileProfileName || "Profile"}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-sm">
                    {mobileProfileInitials ||
                      (profileData
                        ? getProfileDisplay(profileData).initials
                        : "")}
                  </div>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center w-full">
            <form onSubmit={handleSearch} className="relative flex-grow min-w-0">
              <div className="flex items-center shadow-sm w-full border border-[#D2D4D8] rounded-lg focus-within:ring-1 focus-within:ring-blue-500">
                {/* Search Icon */}
                <div className="pl-4 pr-3 py-2 flex items-center">
                  <HiOutlineSearch className="w-4 h-4 text-black/60" />
                </div>

                {/* Input Field */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="flex-1 min-w-0 py-2 pr-2 text-sm border-0 focus:outline-none focus:ring-0"
                  autoFocus
                />

                {/* Dropdown Button */}
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    className="h-full px-4 py-2 text-sm bg-[#EEEEEE] text-black border-l border-[#D2D4D8] flex items-center rounded-r-lg"
                    onClick={() =>
                      setIsMobileSearchTypeOpen(!isMobileSearchTypeOpen)
                    }
                  >
                    {searchTypes.find((t) => t.value === searchType)?.label}
                    <HiChevronDown className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>

              {/* Search Type Dropdown */}
              {isMobileSearchTypeOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg py-1 z-50">
                  {searchTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        setSearchType(type.value);
                        setIsMobileSearchTypeOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              )}
            </form>
            <button
              onClick={() => setIsMobileSearchOpen(false)}
              className="p-2 ml-2 flex-shrink-0"
              aria-label="Close search"
            >
              <HiX className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      <MegaMenuMobile
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        anchorTop={menuAnchorTop}
        navigationItems={navigationItems}
        categories={megaCategories}
        pills={quickAccessPills}
        isActive={isActive}
      />

      <ProfileSidebar
        isOpen={isMobileProfileOpen}
        onClose={() => setIsMobileProfileOpen(false)}
        profileData={profileData}
        getProfileDisplay={getProfileDisplay}
        getProfilePicAccordingToRole={getProfilePicAccordingToRole}
        getInitialsForRole={getInitialsForRole}
        getNameByRole={getNameByRole}
        handleLogout={handleLogout}
        menuItems={menuItems}
        canSwitchRoles={canSwitchRoles}
        handleRoleSwitch={handleRoleSwitch}
        currentRole={currentRole}
        isAgencyOnUI={isAgencyOnUI}
        isRoleSwitching={isRoleSwitching}
      />

      <NotificationSidebar
        isOpen={isNotificationOpen && isMobile}
        onClose={() => setIsNotificationOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        recentNotifications={recentNotifications}
        handleNotificationClick={handleNotificationClick}
        handleMarkAllAsRead={handleMarkAllAsRead}
        formatNotificationTime={formatNotificationTime}
      />
    </>
  );
};

export default MobileHeader;
